import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { actOnException, fetchOpenExceptions } from "../../services/qboExceptionsService";
import { ACTIONS, QuickBooksExceptionRow } from "./QuickBooksExceptionRow";

// The QuickBooks page's Exceptions tab, /support/quickbooks/exceptions
// (DEV-2442): the QuickBooks connector's open exceptions across all clients,
// newest first, and the actions that settle them. The writes go through the
// qbo-exceptions Edge Function. The open count is shared with the page, which
// shows it on the tab.

const NOTICE_STYLE = {
  success: { color: "#1B7F43", background: "#E6F6EC" },
  info: { color: "#3E3E3E", background: "#F0F0F0" },
};

export function QuickBooksExceptionsPage() {
  const setOpenExceptions = useOutletContext()?.setOpenExceptions;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState("loading"); // loading | error | ready (first load only)
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null); // { tone, text }

  // A refresh keeps the current rows on screen until the new ones arrive.
  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const result = await fetchOpenExceptions();
      setRows(result.rows);
      setTotal(result.total);
      setLoadError(null);
      setLoadState("ready");
    } catch (err) {
      setLoadError(err?.message || "Could not load the QuickBooks exceptions.");
      setLoadState((s) => (s === "ready" ? s : "error"));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loadState === "ready") setOpenExceptions?.(total);
  }, [loadState, total, setOpenExceptions]);

  // extra (the chosen customer) is passed through only when the row gives one.
  // A row someone else already handled is stale: reload instead of failing it.
  const act = async (exception, action, ...extra) => {
    setNotice(null);
    try {
      await actOnException(action, exception.id, ...extra);
    } catch (err) {
      if (err?.code !== "already_handled") throw err;
      setNotice({ tone: "info", text: err.message });
      await load();
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== exception.id));
    setTotal((t) => Math.max(0, t - 1));
    setNotice({ tone: "success", text: `${ACTIONS[action].done}: ${exception.stripe_object_id}` });
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#151515", margin: "0 0 6px" }}>Open exceptions</h2>
          <p style={{ color: "#626262", fontSize: 14, margin: 0 }}>Stripe records the connector could not post to QuickBooks, newest first.</p>
        </div>
        <button type="button" onClick={load} disabled={refreshing} style={{ padding: "8px 14px", background: "transparent", color: "#626262", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>Refresh</button>
      </div>

      <ul style={{ fontSize: 12, color: "#626262", margin: "0 0 20px", paddingLeft: 18, lineHeight: 1.6 }}>
        {Object.values(ACTIONS).map(({ label, help }) => (
          <li key={label}><strong style={{ color: "#3E3E3E" }}>{label}</strong>: {help}</li>
        ))}
      </ul>

      {notice && <p role="status" style={{ fontSize: 13, ...NOTICE_STYLE[notice.tone], borderRadius: 8, padding: "8px 12px", margin: "0 0 14px" }}>{notice.text}</p>}

      {loadState === "loading" && <p style={{ fontSize: 13, color: "#626262" }}>Loading…</p>}
      {loadError && <p role="alert" style={{ fontSize: 13, color: "#B42318" }}>{loadError}</p>}
      {loadState === "ready" && total > rows.length && (
        <p style={{ fontSize: 12, color: "#626262", margin: "0 0 10px" }}>{`Showing ${rows.length} of ${total}`}</p>
      )}
      {loadState === "ready" && rows.length === 0 && (
        <p style={{ fontSize: 14, color: "#626262", background: "#FAFAFA", border: "1px solid #F0F0F0", borderRadius: 12, padding: "20px 16px", textAlign: "center" }}>
          No open exceptions. Everything the connector found was posted or handled.
        </p>
      )}
      {loadState === "ready" && rows.map((exception) => (
        <QuickBooksExceptionRow
          key={exception.id}
          exception={exception}
          onAct={(...args) => act(exception, ...args)}
        />
      ))}
    </>
  );
}
